/* eslint-disable camelcase */
/* eslint-disable no-unused-vars */
/* eslint-disable import/no-unresolved */
/**
 * @name Captable Hook
 * @memberof module:common/hooks
 * @type {ReactHook}
 * @return {React} Captable Hook
 */

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isUndefined } from 'lodash';
import { useSnackbar } from 'notistack';
import { isEmpty } from 'validate.js';
import {
  ApiService,
  CapTableDetailsModel,
  CapTablesService,
  CashFlowLedgerItem,
  CustomBreakpoints,
  FundOwnershipCapTableDetailsModel,
  FundOwnershipDetailModel,
  ProFormaCapTableModel,
  SecurityRead,
} from 'api';
import type { AcceptAIFieldRequest } from 'api/models/AcceptAIFieldRequest';
import type { DismissAIFieldRequest } from 'api/models/DismissAIFieldRequest';
import { ERROR_403 } from 'common/config/api';
import { ALLOCATION_VALUES_BY_COMPANY_QUERY_KEY } from 'common/constants/services/companies';
import { useStore } from 'common/store';
import { distributeDirtyPayload } from 'components/CellAnnotationValueViewer';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import { acceptAIFieldsError } from 'pages/CapTable/cap-table/utilities/acceptAIFieldsError';
import { aiFieldAliasForSecurity, aiRequestFieldForSecurity } from 'pages/CapTable/cap-table/utilities/aiFieldMap';
import {
  preserveCitationDrafts,
  type ReferenceSecurity,
  savePendingCitationDrafts,
  withSaveReferences,
} from 'pages/CapTable/cap-table/utilities/referenceWrites';
import { applyAcceptToSecurity, applyDismissToSecurity } from 'pages/CapTable/cap-table/utilities/securityAiPatches';
import { refreshMeasurementDatesByCompany } from 'services/hooks/companies/useGetMeasurementDatesByCompany';
import { useCapTableDirtyCellStore } from 'store/useCapTableDirtyCellStore';
import {
  splitDirtyForCflSave,
  splitDirtyForFodSave,
  splitDirtyForSecuritySave,
  useCellAnnotationStore,
} from 'store/useCellAnnotationStore';
import { CaptableService } from '..';
import { AttributeType, useRetrieveAttributeMapper } from './attributeMapper';
import { useResponse } from './useResponse';
import { captableAction, globalAction } from '../../common/actions';
import { CapTableInfo } from '../../common/types/capTable';
import { useAttributeMapperStore } from '../../store/useAttributeMapperStore';
import { extractResponseErrorMessage } from '../../utilities';
import SheetAttributeManager from '../sheet-attribute-manager/SheetAttributeManager';

/**
 * @function
 * @name useGetCaptableInfo
 * @memberof Captable Hook
 * @description Hook to fetch captable info from captable service
 * @param {integer} dataId the captable id
 * @return {object} structure for store context.
 */

export const useGetCaptableInfo = () => {
  const [globalStore, dispatch] = useStore();
  const [capTableInfo, setUpdatedCapTableInfo] = useState<CapTableInfo>({});
  const inFlightRef = useRef(new Map<number, Promise<boolean>>());

  const { processErrorResponse, errorNotification } = useResponse();
  const { setAttributeMapperManager, clearAttributeMapperManager } = useAttributeMapperStore();
  const [retrieveAttributeMapper] = useRetrieveAttributeMapper();

  const getCapTableInfo = useCallback(
    // shouldApply runs once the response is back: it lets a caller drop a reply
    // for a cap table the user has already navigated away from. Resolves whether the load landed.
    async (newCaptableId, updateGlobalStore = true, force = false, shouldApply = () => true): Promise<boolean> => {
      // Checked up front too: the load blanks the store first, which would wipe a table
      // the caller has already navigated away from.
      if (!newCaptableId || !shouldApply()) {
        return false;
      }
      const existing = inFlightRef.current.get(newCaptableId);
      if (existing && !force) {
        return existing;
      }
      if (existing && force) {
        await existing.catch(() => undefined);
        // Waiting on the queued load is its own window for the selection to change.
        if (!shouldApply()) {
          return false;
        }
      }

      const run = (async () => {
        dispatch(globalAction.showLoadingProgress(true));
        dispatch(captableAction.setCaptableInfo({}));

        const defaultErrorMessage = 'An error occurred while loading the Cap Table information';

        try {
          const captSvc = new CaptableService();
          const capTableInfo = (await captSvc.dataById(newCaptableId)) as unknown as CapTableInfo;
          const mapping = capTableInfo?.sheet_attribute_mapping;
          // A mapper that will not load costs the analyst its highlights, not the cap
          // table itself; it reports its own failure, so this stays quiet.
          const retrievedMapper = mapping
            ? await retrieveAttributeMapper(mapping, AttributeType.CAP_TABLE).catch(() => null)
            : null;

          // Everything is loaded: commit it as one piece, or drop all of it when the
          // caller's guard says the selection has moved on.
          if (!shouldApply()) {
            return false;
          }
          if (isEmpty(capTableInfo)) {
            errorNotification(defaultErrorMessage);
            return false;
          }
          if (updateGlobalStore) {
            dispatch(
              captableAction.setCaptableInfo({
                ...globalStore.captableInfo,
                ...(capTableInfo as unknown as Partial<CapTableInfo>),
              })
            );
            // A full reload replaces the dataset, so local dirty-cell state is obsolete.
            useCapTableDirtyCellStore.getState().clearAll();
          }
          setUpdatedCapTableInfo(capTableInfo);

          if (retrievedMapper) {
            setAttributeMapperManager(new SheetAttributeManager(retrievedMapper));
          } else {
            clearAttributeMapperManager();
          }
          return true;
        } catch (error) {
          processErrorResponse({
            error,
            defaultErrorMessage,
            action: 'get the Cap Table information',
          });
          return false;
        } finally {
          dispatch(globalAction.showLoadingProgress(false));
        }
      })();

      inFlightRef.current.set(newCaptableId, run);
      try {
        return await run;
      } finally {
        if (inFlightRef.current.get(newCaptableId) === run) {
          inFlightRef.current.delete(newCaptableId);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, globalStore.captableInfo]
  );

  return [capTableInfo, getCapTableInfo];
};

/**
 * In-place patch of the cap table securities in the global store.
 * No store blanking, no loading spinner, no refetch - used by AI
 * accept/dismiss and citation actions (SCAL-8959).
 */
export const usePatchCapTableSecurities = () => {
  const [globalStore, dispatch] = useStore();
  const captableInfoRef = useRef(globalStore.captableInfo);
  // The ref updates per render, so two patches dispatched in the same tick would
  // compound on the same snapshot; current consumers are strictly one-at-a-time user actions.
  captableInfoRef.current = globalStore.captableInfo;

  return useCallback(
    (updater: (securities: SecurityRead[]) => SecurityRead[]) => {
      const captableInfo = captableInfoRef.current;
      if (!captableInfo || !Array.isArray(captableInfo.securities)) {
        return;
      }
      dispatch(
        captableAction.setCaptableInfo({
          ...captableInfo,
          securities: updater(captableInfo.securities),
        })
      );
    },
    [dispatch]
  );
};

export const useAcceptAIFields = () => {
  const patchSecurities = usePatchCapTableSecurities();
  const [globalStore] = useStore();
  const { processErrorResponse } = useResponse();

  const acceptAIFields = useCallback(
    async (securityId: number, payload: AcceptAIFieldRequest) => {
      const defaultErrorMessage = 'An error occurred while accepting AI fields';
      let fieldError: string | undefined;

      const target = globalStore.captableInfo?.securities?.find((security: SecurityRead) => security.id === securityId);
      const request = payload.field_names
        ? { ...payload, field_names: payload.field_names.map(field => aiRequestFieldForSecurity(target, field)) }
        : payload;
      try {
        const response = await CapTablesService.apiCapTablesSecuritiesAcceptAiFieldsPartialUpdate(
          String(securityId),
          request
        );
        const fieldNames = response.accepted_fields;
        fieldError = acceptAIFieldsError(response, request.field_names ?? []);
        if (fieldNames.length) {
          patchSecurities(securities =>
            securities.map(security => {
              if (security.id !== securityId) {
                return security;
              }
              fieldNames.forEach(field => useCapTableDirtyCellStore.getState().clearField(securityId, aiFieldAliasForSecurity(security, field)));
              return applyAcceptToSecurity(security, fieldNames, securities) as SecurityRead;
            })
          );
        }
        if (fieldError) {
          throw new Error(fieldError);
        }
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage: fieldError ?? defaultErrorMessage,
          action: 'accept AI fields',
        });
        throw error;
      }
    },
    [patchSecurities, processErrorResponse, globalStore.captableInfo?.securities]
  );

  return { acceptAIFields };
};

export const useAcceptAllAIFields = () => {
  const patchSecurities = usePatchCapTableSecurities();
  const { processErrorResponse } = useResponse();
  const { enqueueSnackbar } = useSnackbar();

  const acceptAllAIFields = useCallback(
    async (capTableId: number) => {
      const defaultErrorMessage = 'An error occurred while accepting all AI fields';

      try {
        const { results } = await CapTablesService.apiCapTablesAcceptAllAiFieldsPartialUpdate(String(capTableId));
        // Apply ONLY the fields the backend reports as accepted, per security, so a
        // partial success (some securities errored) still reflects the ones that
        // saved instead of leaving the whole grid stale until a hard refresh.
        const acceptedBySecurity = new Map(results.map(r => [r.security_id, r.accepted_fields]));
        patchSecurities(securities =>
          securities.map(s => {
            const fieldNames = s.id != null ? acceptedBySecurity.get(s.id) : undefined;
            if (!fieldNames?.length) {
              return s;
            }
            fieldNames.forEach(f => useCapTableDirtyCellStore.getState().clearField(s.id as number, aiFieldAliasForSecurity(s, f)));
            return applyAcceptToSecurity(s, fieldNames, securities) as SecurityRead;
          })
        );

        const failedCount = results.filter(r => r.errors.length).length;
        if (failedCount) {
          enqueueSnackbar(
            `Accepted the fields we could. ${failedCount} ${
              failedCount === 1 ? 'security' : 'securities'
            } could not be accepted and were left unchanged.`,
            { variant: 'warning' }
          );
        }
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage,
          action: 'accept all AI fields',
        });
        throw error;
      }
    },
    [patchSecurities, processErrorResponse, enqueueSnackbar]
  );

  return { acceptAllAIFields };
};

export const useDismissAIFields = () => {
  const patchSecurities = usePatchCapTableSecurities();
  const [globalStore] = useStore();
  const { processErrorResponse } = useResponse();

  const dismissAIFields = useCallback(
    async (securityId: number, payload: DismissAIFieldRequest): Promise<{ securityDeleted: boolean }> => {
      const defaultErrorMessage = 'An error occurred while dismissing AI fields';
      const target = globalStore.captableInfo?.securities?.find((security: SecurityRead) => security.id === securityId);
      const request = payload.field_names
        ? { ...payload, field_names: payload.field_names.map(field => aiRequestFieldForSecurity(target, field)) }
        : payload;

      try {
        const response = await CapTablesService.apiCapTablesSecuritiesDismissAiFieldsPartialUpdate(
          String(securityId),
          request
        );
        const securityDeleted = Boolean(response.security_deleted);
        const fieldNames = response.dismissed_fields;
        patchSecurities(securities => {
          const target = securities.find(s => s.id === securityId);
          if (!target) {
            return securities;
          }
          fieldNames.forEach(field =>
            useCapTableDirtyCellStore.getState().clearField(securityId, aiFieldAliasForSecurity(target, field))
          );
          if (securityDeleted) {
            // This decline dismissed the LAST finding of an AI-CREATED security, so
            // the server deleted the security itself. Mirror by dropping it from the
            // store: grid syncs stop rendering its column, and a reopen-driven
            // un-delete resurfaces it on refetch instead of the local merge pinning
            // it deleted forever.
            return securities.filter(s => s.id !== securityId);
          }
          // `securities` doubles as the sibling pool: a declined underlying-security
          // value reverts within the cell's ref domain (securityAiPatches, SCAL-9337).
          return securities.map(s =>
            s.id === securityId ? (applyDismissToSecurity(s, fieldNames, securities) as SecurityRead) : s
          );
        });
        if (response.errors.length || request.field_names?.some(field => !fieldNames.includes(field))) {
          throw new Error(response.errors.join(' ') || 'Failed to dismiss the suggestion');
        }
        return { securityDeleted };
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage,
          action: 'dismiss AI fields',
        });
        throw error;
      }
    },
    [patchSecurities, processErrorResponse, globalStore.captableInfo?.securities]
  );

  return { dismissAIFields };
};

/**
 * @function
 * @name useGetBreakpointAnalysis
 * @description Get the breakpoint analysis for a specific Cap Table by its id
 * @return {array}
 */
export const useGetBreakpointAnalysis = () => {
  const [globalStore, dispatch] = useStore();
  const [capTableInfo, setUpdatedCapTableInfo] = useState<CapTableInfo | string>({});

  const { processErrorResponse, errorNotification } = useResponse();

  const getCapTableBreakpoints = useCallback(
    async newCaptableId => {
      if (newCaptableId) {
        dispatch(globalAction.showLoadingProgress(true));

        const defaultErrorMessage = 'An error occurred while loading the Breakpoint Analysis information';

        try {
          const captSvc = new CaptableService();
          const breakpointAnalysis = (await captSvc.getAllBreakpointsData(
            newCaptableId
          )) as unknown as CapTableDetailsModel;

          if (breakpointAnalysis) {
            let prevCaptableInfo = { ...globalStore.captableInfo };

            if (isEmpty(prevCaptableInfo)) {
              const captSvc = new CaptableService();
              const updatedCaptableInfo = await captSvc.dataById(newCaptableId);

              if (updatedCaptableInfo) {
                prevCaptableInfo = { ...updatedCaptableInfo };
              }
            }

            const captable = {
              ...prevCaptableInfo,
              waterfall_breakpoints: breakpointAnalysis.waterfall_breakpoints,
              custom_breakpoints: breakpointAnalysis.custom_breakpoints,
            };
            dispatch(captableAction.setCaptableInfo(captable));
            setUpdatedCapTableInfo(captable);
          } else {
            errorNotification(defaultErrorMessage);
          }
        } catch (error: any) {
          if (error.status === 403) {
            setUpdatedCapTableInfo(ERROR_403);
          } else {
            processErrorResponse({ error, defaultErrorMessage });
          }
        } finally {
          dispatch(globalAction.showLoadingProgress(false));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, globalStore.captableInfo?.id]
  );

  return { capTableInfo, getCapTableBreakpoints };
};

export const useUpdateCapTableAndSecuritiesInfo = () => {
  const [data, setData] = useState({});
  const reloadRequiredErrors = useRef(new Map<number, unknown>());

  const captSvc = new CaptableService();

  const [globalStore, dispatch] = useStore();
  const { setAction } = useContext(UnsavedChangesContext);

  const { processErrorResponse, successNotification } = useResponse();

  const getErrorInfoFromResponse = (error: any, defaultErrorMessage: string) => {
    const servicePath = error?.response?.req?.url?.split('/').pop();
    const errorMessage =
      servicePath === 'securities' ? (extractResponseErrorMessage(error) ?? defaultErrorMessage) : defaultErrorMessage;
    return { errorMessage, servicePath };
  };

  // React Query Client
  const queryClient = useQueryClient();

  const updateCapTableData = async (
    captableId: number,
    securities: SecurityRead[],
    captableInfo: CapTableDetailsModel,
    updateGlobalStore: boolean
  ) => {
    if (captableId) {
      dispatch(globalAction.showLoadingProgress(true));

      let defaultErrorMessage = 'An error occurred while updating Cap Table information';
      let updatedCap = { ...globalStore.captableInfo };
      let savedSecurities: SecurityRead[] | undefined;
      let savingCitations = false;

      const applySavedCapTable = (updatedCapTableInfo: CapTableInfo) => {
        // Committed ledger ids must replace the pre-save edits before the store refresh merges them.
        useCapTableDirtyCellStore.getState().clearAll();
        useCellAnnotationStore.getState().clearDirty('captable:');
        useCellAnnotationStore.getState().clearDirty('security:');
        setData(updatedCapTableInfo);
        if (updateGlobalStore) {
          dispatch(captableAction.setCaptableInfo({ ...globalStore.captableInfo, ...updatedCapTableInfo }));
        }
      };

      try {
        const reloadError = reloadRequiredErrors.current.get(captableId);
        if (reloadError) {
          throw reloadError;
        }

        // Fold captable-scoped cell-annotation edits into the PUT body so the
        // backend persists them in the same transaction as the cap-table save.
        // Wire shape: { cell_annotations: { field_alias: { annotation_type: entry } } }.
        const dirtyAnnotations = useCellAnnotationStore.getState().collectDirtyPayload();
        const { capTableByParentId } = distributeDirtyPayload(dirtyAnnotations);
        const captableAnnotationsWire = capTableByParentId[String(captableId)];

        if (!isUndefined(captableInfo) || captableAnnotationsWire) {
          const captableBody: any = { ...(captableInfo ?? {}) };
          if (captableAnnotationsWire) {
            captableBody.cell_annotations = captableAnnotationsWire;
          }
          updatedCap = await captSvc.updateCaptableById(captableId, captableBody);
        }

        if (securities?.length > 0) {
          defaultErrorMessage = 'An error occurred while updating Cap Table securities';

          // Fold each security's dirty Pre-FX entry onto its payload, keyed by
          // persisted id or `ref_<security_ref>` for unsaved new columns. The
          // synthetic key matches the wire-time ref built in conditions.tsx
          // (`parentRef = cell.columnRef ?? security_ref`, which resolves to
          // the column's `security_ref`), so persisted and new columns both
          // line up with the splitter's bucket keys.
          const { securityCellAnnotations } = splitDirtyForSecuritySave(dirtyAnnotations);
          const lookupSecurityEntry = (sec: SecurityRead) => {
            if (sec?.id != null && sec.id !== 0) {
              const byId = securityCellAnnotations[String(sec.id)];
              if (byId !== undefined) {
                return byId;
              }
            }
            return sec?.security_ref ? securityCellAnnotations[`ref_${sec.security_ref}`] : undefined;
          };

          const snakeCased = securities.map(item => {
            const annotations = lookupSecurityEntry(item);
            return {
              ...withSaveReferences(item),
              ...(annotations ? { cell_annotations: annotations } : {}),
            };
          });

          savedSecurities = preserveCitationDrafts(
            (await captSvc.updateSecuritiesById(captableId, snakeCased)) as SecurityRead[], securities
          );
          savingCitations = true;
          await savePendingCitationDrafts(savedSecurities as unknown as ReferenceSecurity[], captSvc.createSecurityCitation);
          savingCitations = false;
          updatedCap = await captSvc.getCaptableById(captableId);
        }

        applySavedCapTable({ ...updatedCap, securities: savedSecurities ?? globalStore.captableInfo?.securities ?? [] });
        successNotification('Cap Table information updated successfully');
        setAction(false);
      } catch (error: any) {
        const body = error?.response?.body;
        const savedInputs = body?.code === 'captable_save_incomplete' && body.inputs_saved === true ? body : undefined;
        const committedSecurities = savedInputs?.securities ?? savedSecurities;
        if (Array.isArray(committedSecurities)) {
          applySavedCapTable({
            ...updatedCap,
            securities: preserveCitationDrafts(committedSecurities, securities, Boolean(savedInputs?.citation_references)),
          });
        } else if (savedInputs?.reload_required) {
          reloadRequiredErrors.current.set(captableId, error);
        }
        const { errorMessage, servicePath } = getErrorInfoFromResponse(error, defaultErrorMessage);
        const action = servicePath === 'securities' ? 'update Cap Table securities' : 'update Cap Table information';
        const committedErrorMessage = savingCitations
          ? `Cap table inputs were saved, but some citations could not be attached. ${String(error.message).replace(/\.+$/, '')}. Save again to retry, or remove a pending citation from its citation menu and select new text.`
          : 'Cap table inputs were saved, but updated details could not be loaded. Reload the page before making further changes.';
        const detail = savedInputs?.detail ?? (savedSecurities
          ? committedErrorMessage
          : errorMessage);
        const citationMessage = extractResponseErrorMessage({ response: { body: savedInputs?.citation_references } });
        processErrorResponse({
          error,
          defaultErrorMessage: [detail, citationMessage].filter(Boolean).join(' '),
          action,
        });
        throw error;
      } finally {
        // Invalidate the Allocation Values query to refetch the data with the new values
        queryClient.invalidateQueries({ queryKey: [ALLOCATION_VALUES_BY_COMPANY_QUERY_KEY] });
        // Reallocation may have failed; refresh the badge
        refreshMeasurementDatesByCompany(queryClient);

        dispatch(globalAction.showLoadingProgress(false));
      }
    }
  };

  return [data, updateCapTableData];
};

/**
 * @function
 * @name useUpdateCaptableInfo
 * @memberof Captable Hook
 * @description Hook to update captable info from captable service
 * @param {integer} dataId the captable id
 * @param {object} captableInfo captable structure data
 * @return {object} structure for store context.
 */
export const useUpdateCaptableInfo = ({
  captableId,
  captableInfo,
}: {
  captableId?: number | null;
  captableInfo?: unknown;
}): [CapTableDetailsModel, (newCaptableId?: number | null, newCaptableInfo?: unknown) => Promise<void>] => {
  const [globalStore, dispatch] = useStore();
  const [data, setData] = useState<CapTableDetailsModel>({} as CapTableDetailsModel);
  const prevCaptableInfo = useRef(globalStore.captableInfo);

  const { processErrorResponse, errorNotification, successNotification } = useResponse();

  const updateCapTableData = useCallback(
    async (newCaptableId, newCaptableInfo) => {
      if (newCaptableId && newCaptableInfo) {
        dispatch(globalAction.showLoadingProgress(true));

        const defaultErrorMessage = 'An error occurred while updating Cap Table information';

        try {
          const captSvc = new CaptableService();
          const captableInfo = (await captSvc.updateCaptableById(
            newCaptableId,
            newCaptableInfo
          )) as unknown as CapTableDetailsModel;
          if (!isEmpty(captableInfo)) {
            dispatch(
              captableAction.setCaptableInfo({
                ...prevCaptableInfo.current,
                ...(captableInfo as unknown as Partial<CapTableInfo>),
              })
            );
            setData(captableInfo);
            successNotification('Cap Table updated successfully');
          } else {
            errorNotification(defaultErrorMessage);
          }
        } catch (error) {
          processErrorResponse({
            error,
            defaultErrorMessage,
            action: 'update the Cap Table',
          });
        } finally {
          dispatch(globalAction.showLoadingProgress(false));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, prevCaptableInfo]
  );

  useEffect(() => {
    updateCapTableData(captableId, captableInfo);
  }, [captableId, captableInfo, updateCapTableData]);

  return [data, updateCapTableData];
};

/**
 * @function
 * @name useUpdateCaptableSecurities
 * @memberof Captable Hook
 * @description Hook to update captable info from Cap Table service
 * @param {INTEGER} dataId the captable id
 * @param {OBJECT} captableSecurititesInfo captable structure data
 * @return {OBJECT} structure for store context.
 */
export const useUpdateCaptableSecurities = () => {
  const captSvc = new CaptableService();
  const [globalStore, dispatch] = useStore();
  const [data, setData] = useState({});

  const { processErrorResponse, errorNotification, successNotification } = useResponse();

  const updateSecuritiesData = async (newCaptableId: number, newCaptableSecurititesInfo: SecurityRead[]) => {
    dispatch(globalAction.showLoadingProgress(true));

    const defaultErrorMessage = 'An error occurred while updating the Cap Table securities';

    try {
      const captableSecuritiesInfo = await captSvc.updateSecuritiesById(newCaptableId, newCaptableSecurititesInfo);
      if (captableSecuritiesInfo) {
        dispatch(
          captableAction.setCaptableInfo({
            ...globalStore.captableInfo,
            securities: captableSecuritiesInfo,
          })
        );
        setData(captableSecuritiesInfo);
        successNotification('Cap Table securities updated successfully');
      } else {
        errorNotification(defaultErrorMessage);
      }
    } catch (error) {
      processErrorResponse({
        error,
        defaultErrorMessage,
        action: 'update the Cap Table securities',
      });
    } finally {
      dispatch(globalAction.showLoadingProgress(false));
    }
  };
  return [{ captableSecuritiesInfo: data }, updateSecuritiesData];
};

export const useUpdateCaptableFundOwnership = () => {
  const [updatedFundOwnership, setUpdatedFundOwnership] = useState({});

  const [globalStore, dispatch] = useStore();
  const { setAction } = useContext(UnsavedChangesContext);

  const { processErrorResponse, successNotification } = useResponse();

  // React Query Client
  const queryClient = useQueryClient();

  const isSecurityUsedInFod = (fundOwnershipDetail: FundOwnershipCapTableDetailsModel[], security: SecurityRead) =>
    fundOwnershipDetail.some(
      (fod: any) =>
        fod.security_id === security.id ||
        security.convertible_notes.map(cn => `${security.name} - ${cn.note_name}`).includes(fod.security_name)
    );

  const updateFundOwnershipData = async (captableId: number, fundOwnershipInfo: FundOwnershipDetailModel) => {
    if (captableId && fundOwnershipInfo) {
      dispatch(globalAction.showLoadingProgress(true));

      // Fold every dirty cell-annotation entry onto the nested write payload.
      // PRE_FX, cross_reference, and any future annotation type all ride on
      // the same `cell_annotations` field on each parent - the backend
      // peeks for a PRE_FX entry to derive the FX host column, then upserts
      // the full map through the standard cell-annotations path.
      const dirtyAnnotations = useCellAnnotationStore.getState().collectDirtyPayload();
      const { fodCellAnnotations, acquisitionCellAnnotations, saleCellAnnotations, distributionCellAnnotations } =
        splitDirtyForFodSave(dirtyAnnotations);
      // A child row carries either a persisted `id` (numeric, used as the
      // bucket key) OR a synthetic key derived from its ref UUID
      // (`ref_<uuid>`, set by `applyPreFxAnnotation` for unsaved rows). Look
      // by id first, then by `ref_<rowRef>` so a fresh row's annotations
      // ship in the same payload that creates it.
      const lookupChildEntry = <T,>(
        bucket: Record<string, T>,
        id: number | string | null | undefined,
        rowRef: string | null | undefined
      ): T | undefined => {
        if (id != null && id !== 0 && id !== '') {
          const byId = bucket[String(id)];
          if (byId !== undefined) {
            return byId;
          }
        }
        return rowRef ? bucket[`ref_${rowRef}`] : undefined;
      };
      const withAnnotations = <T extends object>(row: T, annotations: unknown): T =>
        annotations ? ({ ...row, cell_annotations: annotations } as T) : { ...row };
      const enrichedFundOwnershipInfo: any = { ...fundOwnershipInfo };
      if (Array.isArray(enrichedFundOwnershipInfo.fund_ownership_detail)) {
        // Drop synthetic CFL-only FOD entries (flagged `historical_record:
        // true` by the cap-table serializer). They have no row in the
        // database, including them in the PUT payload would tell the
        // backend to update non-existent records.
        enrichedFundOwnershipInfo.fund_ownership_detail = enrichedFundOwnershipInfo.fund_ownership_detail
          .filter((fod: any) => !fod?.historical_record)
          .map((fod: any) => {
            const enrichedFod: any = withAnnotations(
              fod,
              fod?.id != null ? fodCellAnnotations[String(fod.id)] : undefined
            );

            if (Array.isArray(enrichedFod.acquisitions)) {
              enrichedFod.acquisitions = enrichedFod.acquisitions.map((acq: any) => {
                const updatedAcq: any = withAnnotations(
                  acq,
                  lookupChildEntry(acquisitionCellAnnotations, acq?.id, acq?.acquisition_ref)
                );

                if (Array.isArray(acq?.sales)) {
                  updatedAcq.sales = acq.sales.map((sale: any) =>
                    withAnnotations(sale, lookupChildEntry(saleCellAnnotations, sale?.id, sale?.sale_ref))
                  );
                }

                if (Array.isArray(acq?.cash_distributions)) {
                  // Cash distributions don't have a stable ref field today;
                  // only persisted (id-bearing) rows route here.
                  updatedAcq.cash_distributions = acq.cash_distributions.map((dist: any) =>
                    withAnnotations(dist, lookupChildEntry(distributionCellAnnotations, dist?.id, null))
                  );
                }

                return updatedAcq;
              });
            }
            return enrichedFod;
          });
      }

      try {
        const fundOwnershipResponse = await ApiService.apiCapTablesFundOwnershipUpdate(
          captableId,
          enrichedFundOwnershipInfo
        );

        const { fund_ownership_detail } = fundOwnershipResponse;

        // The response includes the fields fund_ownership_id, fund_id, and security_id,
        // but the frontend is expecting fund_ownership, fund, and security, respectively.
        const tmpFundOwnershipDetail = fund_ownership_detail.map((fod: any) => ({
          ...fod,
          fund: fod.fund_id,
          security: fod.security_id,
          fund_ownership: fod.fund_ownership_id,
        }));

        const tmpFundOwnershipInfo = {
          ...globalStore.captableInfo.fund_ownership,
          ...fundOwnershipResponse,
          fund_ownership_detail: tmpFundOwnershipDetail,
        };

        dispatch(
          captableAction.setCaptableInfo({
            ...globalStore.captableInfo,
            securities: globalStore.captableInfo.securities.map((security: SecurityRead) => ({
              ...security,
              is_used_in_fod: isSecurityUsedInFod(tmpFundOwnershipDetail, security),
            })),
            fund_ownership: tmpFundOwnershipInfo,
          })
        );

        setAction(false);
        // Clear dirty across every parent type the FOD writer flushes - the
        // FOD payload carries cross-reference for FOD parents and Pre-FX for
        // each child writable. clearDirty is per-prefix; we call it once per
        // type to avoid clobbering unrelated cap-table entries.
        const annotationStore = useCellAnnotationStore.getState();
        annotationStore.clearDirty('fundownershipdetail:');
        annotationStore.clearDirty('sharesacquisition:');
        annotationStore.clearDirty('sharessale:');
        annotationStore.clearDirty('cashdistribution:');
        successNotification('Fund Ownership was updated successfully');
        setUpdatedFundOwnership(tmpFundOwnershipInfo);
      } catch (error: any) {
        const defaultErrorMessage =
          extractResponseErrorMessage(error) ??
          'An error occurred while updating the Cap Table Fund Ownership';
        processErrorResponse({
          error,
          defaultErrorMessage,
          action: 'update the Cap Table Fund Ownership',
        });
      } finally {
        // Invalidate the Allocation Values query to refetch the data with the new values
        queryClient.invalidateQueries({ queryKey: [ALLOCATION_VALUES_BY_COMPANY_QUERY_KEY] });
        // Reallocation may have failed; refresh the badge
        refreshMeasurementDatesByCompany(queryClient);

        dispatch(globalAction.showLoadingProgress(false));
      }
    }
  };
  return { updatedFundOwnership, updateFundOwnershipData };
};

// Clones the primary cap table's fund ownership onto a pro forma version that has none yet.
export const useCopyPrimaryCaptableFundOwnership = () => {
  const [globalStore, dispatch] = useStore();
  const { processErrorResponse, successNotification } = useResponse();
  const queryClient = useQueryClient();

  const isSecurityUsedInFod = (fundOwnershipDetail: FundOwnershipCapTableDetailsModel[], security: SecurityRead) =>
    fundOwnershipDetail.some(
      (fod: any) =>
        fod.security_id === security.id ||
        security.convertible_notes.map(cn => `${security.name} - ${cn.note_name}`).includes(fod.security_name)
    );

  const copyPrimaryFundOwnership = async (captableId: number) => {
    dispatch(globalAction.showLoadingProgress(true));

    try {
      const fundOwnershipResponse = await ApiService.apiCapTablesFundOwnershipCopyFromPrimaryCreate(captableId);
      const { fund_ownership_detail } = fundOwnershipResponse;

      // The response includes fund_ownership_id, fund_id, and security_id, but the frontend
      // expects fund_ownership, fund, and security respectively (matches updateFundOwnershipData).
      const tmpFundOwnershipDetail = fund_ownership_detail.map((fod: any) => ({
        ...fod,
        fund: fod.fund_id,
        security: fod.security_id,
        fund_ownership: fod.fund_ownership_id,
      }));

      const tmpFundOwnershipInfo = {
        ...globalStore.captableInfo.fund_ownership,
        ...fundOwnershipResponse,
        fund_ownership_detail: tmpFundOwnershipDetail,
      };

      dispatch(
        captableAction.setCaptableInfo({
          ...globalStore.captableInfo,
          securities: globalStore.captableInfo.securities.map((security: SecurityRead) => ({
            ...security,
            is_used_in_fod: isSecurityUsedInFod(tmpFundOwnershipDetail, security),
          })),
          fund_ownership: tmpFundOwnershipInfo,
        })
      );

      successNotification('Fund ownership was copied from the primary cap table');
      return tmpFundOwnershipInfo;
    } catch (error: any) {
      const defaultErrorMessage = error?.body?.detail ?? 'An error occurred while copying the Cap Table Fund Ownership';
      processErrorResponse({
        error,
        defaultErrorMessage,
        action: 'copy the Cap Table Fund Ownership',
      });
      return null;
    } finally {
      queryClient.invalidateQueries({ queryKey: [ALLOCATION_VALUES_BY_COMPANY_QUERY_KEY] });
      // Reallocation may have failed; refresh the badge
      refreshMeasurementDatesByCompany(queryClient);
      dispatch(globalAction.showLoadingProgress(false));
    }
  };

  return { copyPrimaryFundOwnership };
};

export const useCaptableCashFlowLedger = () => {
  const [cashFlowLedger, setCashFlowLedger] = useState<CashFlowLedgerItem[]>([]);

  const [globalStore, dispatch] = useStore();
  const captableInfoRef = useRef(globalStore.captableInfo);

  useEffect(() => {
    captableInfoRef.current = globalStore.captableInfo;
  }, [globalStore.captableInfo]);

  const { setAction } = useContext(UnsavedChangesContext);
  const { processErrorResponse, successNotification } = useResponse();

  const sortCashFlowsByDate = useCallback(
    (flows: CashFlowLedgerItem[]) =>
      [...flows].sort((a, b) => {
        const dateA = a.date_of_transaction ? new Date(a.date_of_transaction).getTime() : 0;
        const dateB = b.date_of_transaction ? new Date(b.date_of_transaction).getTime() : 0;
        return dateA - dateB;
      }),
    []
  );

  const fetchCashFlowLedger = useCallback(
    async (captableId?: number) => {
      if (!captableId) {
        return [];
      }

      dispatch(globalAction.showLoadingProgress(true));

      const defaultErrorMessage = 'An error occurred while loading the Cash Flow Ledger';

      try {
        const cashFlowLedgerResponse = await ApiService.apiCapTablesCashFlowLedgerList(captableId);
        const ledgerItems = sortCashFlowsByDate(cashFlowLedgerResponse?.items ?? []);

        const currentCaptableInfo = captableInfoRef.current ?? {};
        dispatch(
          captableAction.setCaptableInfo({
            ...currentCaptableInfo,
            cash_flow_ledger: ledgerItems,
          })
        );
        setCashFlowLedger(ledgerItems);
        return ledgerItems;
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage,
          action: 'load the Cash Flow Ledger',
        });
        return [];
      } finally {
        dispatch(globalAction.showLoadingProgress(false));
      }
    },
    [dispatch, processErrorResponse, sortCashFlowsByDate]
  );

  const updateCashFlowLedger = useCallback(
    async (captableId: number | undefined, ledgerItems: CashFlowLedgerItem[]) => {
      if (!captableId || !ledgerItems) {
        return [];
      }

      dispatch(globalAction.showLoadingProgress(true));

      const defaultErrorMessage = 'An error occurred while updating the Cash Flow Ledger';

      // Fold every dirty cell-annotation entry onto its matching CFL item by
      // id. Virtual rows are filtered out by the reverse parser before they
      // reach here; new (no-id) rows can't be annotated by the UI yet so
      // they're a clean miss. Backend's CashFlowLedgerItemSerializer peeks
      // for a PRE_FX entry to derive the FX host column, then upserts the
      // whole map through the standard cell-annotations path.
      const dirtyAnnotations = useCellAnnotationStore.getState().collectDirtyPayload();
      const { cflCellAnnotations } = splitDirtyForCflSave(dirtyAnnotations);

      try {
        const itemsWithCapTable = ledgerItems.map(item => {
          const baseItem = { ...item, cap_table: captableId };
          const annotations = item.id != null ? cflCellAnnotations[String(item.id)] : undefined;
          return annotations ? { ...baseItem, cell_annotations: annotations } : baseItem;
        });
        const cashFlowLedgerResponse = await ApiService.apiCapTablesCashFlowLedgerBulkSaveCreate(captableId, {
          items: itemsWithCapTable,
        });

        const updatedLedger = sortCashFlowsByDate(cashFlowLedgerResponse?.items ?? ledgerItems);

        const currentCaptableInfo = captableInfoRef.current ?? {};
        dispatch(
          captableAction.setCaptableInfo({
            ...currentCaptableInfo,
            cash_flow_ledger: updatedLedger,
          })
        );

        setAction(false);
        // Clear dirty for CFL Pre-FX entries. The annotation store keys live
        // under the `cashflowledgeritem:` prefix; clearing only that prefix
        // leaves any unrelated cap-table entries (e.g. concurrent FOD edits)
        // untouched.
        useCellAnnotationStore.getState().clearDirty('cashflowledgeritem:');
        successNotification('Cash Flow Ledger updated successfully');
        setCashFlowLedger(updatedLedger);
        return updatedLedger;
      } catch (error) {
        const errorMessage = extractResponseErrorMessage(error);
        processErrorResponse({
          error,
          defaultErrorMessage: errorMessage ?? defaultErrorMessage,
          action: 'update the Cash Flow Ledger',
        });
        throw error;
      } finally {
        dispatch(globalAction.showLoadingProgress(false));
      }
    },
    [dispatch, processErrorResponse, setAction, sortCashFlowsByDate, successNotification]
  );

  return { cashFlowLedger, fetchCashFlowLedger, updateCashFlowLedger };
};

/**
 * @function
 * @name useUpdateCaptableCustomBreakpoints
 * @memberof Captable Hook
 * @description Hook to update captable custom breakpoints from Cap Table service
 * @param {INTEGER} captableId the captable id
 * @param {OBJECT} breakpointsData captable custom BPs data
 */
export const useUpdateCaptableCustomBreakpoints = () => {
  const [updatedCustomBreakpoints, setUpdatedCustomBreakpoints] = useState({});

  const [globalStore, dispatch] = useStore();
  const { setAction } = useContext(UnsavedChangesContext);

  const { processErrorResponse, successNotification } = useResponse();

  // React Query Client
  const queryClient = useQueryClient();

  const updateCustomBreakpoints = useCallback(
    async (captableId: number, breakpointsData: CustomBreakpoints) => {
      if (captableId && breakpointsData) {
        dispatch(globalAction.showLoadingProgress(true));

        try {
          const breakpointsResponse = await ApiService.apiCapTablesAllBreakpointsUpdate(captableId, breakpointsData);

          const hasUnconfiguredCustomBreakpoints =
            breakpointsResponse.use_custom_breakpoints === true &&
            (breakpointsResponse.custom_breakpoints?.breakpoints ?? []).some((bp: any) =>
              (bp.series ?? []).some((s: any) => s.value === null)
            );

          dispatch(
            captableAction.setCaptableInfo({
              ...globalStore.captableInfo,
              ...breakpointsResponse,
              has_unconfigured_custom_breakpoints: hasUnconfiguredCustomBreakpoints,
            })
          );

          setAction(false);
          successNotification('Breakpoints were updated successfully');
          setUpdatedCustomBreakpoints(breakpointsResponse);
        } catch (error) {
          const defaultErrorMessage = 'An error occurred while updating the breakpoints';
          processErrorResponse({
            error,
            defaultErrorMessage,
            action: 'update the Cap Table breakpoints',
          });
        } finally {
          // Invalidate the Allocation Values query to refetch the data with the new values
          queryClient.invalidateQueries({ queryKey: [ALLOCATION_VALUES_BY_COMPANY_QUERY_KEY] });
          // Reallocation may have failed; refresh the badge
          refreshMeasurementDatesByCompany(queryClient);

          dispatch(globalAction.showLoadingProgress(false));
        }
      }
    },
    [dispatch, globalStore.captableInfo, setAction, successNotification, processErrorResponse, queryClient]
  );
  return { updatedCustomBreakpoints, updateCustomBreakpoints };
};

export const useCreateCapTableProforma = () => {
  const [proformaInfo, setProformaInfo] = useState<ProFormaCapTableModel>({} as ProFormaCapTableModel);
  const [isCreatingProforma, setIsCreatingProforma] = useState(false);

  const captSvc = new CaptableService();

  const [, dispatch] = useStore();
  const { setAction } = useContext(UnsavedChangesContext);

  const { processErrorResponse, successNotification } = useResponse();

  // React Query Client
  const queryClient = useQueryClient();

  const createProforma = async (
    proformaInfoPayload: ProFormaCapTableModel,
    onAfterCreate?: (newCap: ProFormaCapTableModel) => Promise<void>
  ) => {
    if (proformaInfoPayload) {
      dispatch(globalAction.showLoadingProgress(true));
      setIsCreatingProforma(true);

      try {
        const response = (await captSvc.createCapTableProforma(
          proformaInfoPayload
        )) as unknown as ProFormaCapTableModel;

        if (onAfterCreate) {
          await onAfterCreate(response);
        }

        setAction(false);
        setProformaInfo(response);
        successNotification('The new proforma was created successfully');
      } catch (error) {
        const defaultErrorMessage = 'An error occurred while creating the new proforma';
        processErrorResponse({
          error,
          defaultErrorMessage,
          action: 'create new proforma',
        });
      } finally {
        // Invalidate the Allocation Values query to refetch the data with the new values
        queryClient.invalidateQueries({ queryKey: [ALLOCATION_VALUES_BY_COMPANY_QUERY_KEY] });

        dispatch(globalAction.showLoadingProgress(false));
        setIsCreatingProforma(false);
      }
    }
  };
  return [proformaInfo, setProformaInfo, createProforma, isCreatingProforma] as const;
};

export const useGetPrimaryCaptableByCompanyDate = () => {
  const [, dispatch] = useStore();
  const [capTableInfo, setPrimaryCaptable] = useState<CapTableDetailsModel>({} as CapTableDetailsModel);

  const { processErrorResponse, errorNotification } = useResponse();

  const getPrimaryCaptable = useCallback(
    async (companyDate: string) => {
      if (companyDate) {
        dispatch(globalAction.showLoadingProgress(true));

        const defaultErrorMessage = 'An error occurred while loading the Cap Table information';

        try {
          const captSvc = new CaptableService();
          const capTableInfo = await captSvc.getPrimaryCaptableByCompanyDate(companyDate);

          if (!isEmpty(capTableInfo)) {
            setPrimaryCaptable(capTableInfo as unknown as CapTableDetailsModel);
            dispatch(captableAction.setCaptableInfo(capTableInfo as unknown as CapTableDetailsModel));
          } else {
            errorNotification(defaultErrorMessage);
          }
        } catch (error) {
          processErrorResponse({
            error,
            defaultErrorMessage,
            action: 'get the Cap Table information',
          });
        } finally {
          dispatch(globalAction.showLoadingProgress(false));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  return [capTableInfo, getPrimaryCaptable];
};
